//Hooks
import { get } from 'svelte/store';
//Formating
import { format } from 'svelte-i18n';
//Helper
import { real_id } from '../../../helpers/interface_helpers';
import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
//Services
import { BytesChunkRequest, gen2Request } from '../../../services/gen_2_http_reqs';
import { retryWithBackoff } from '../../../helpers/retry_helpers';
import { open_external_link_android } from '../../../services/native_container_requests_android';
import { open_external_link } from '../../../services/native_container_requests_ios';
//Stores
import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
import { os, app } from 'src/stores/session/native-bridge';
//Types
import type { Device } from '../../../types/Device';
import type { Script, ScriptsRequest } from '../../../types/requests/settings/scripts';

export class ScripsHelper extends EventTarget {
	private _device: Device;
	private _deviceID: string;
	private _format: any;
	private _saveScripts: boolean;
	constructor(device: Device) {
		super();
		this._device = device;
		this._format = get(format);
		this._deviceID = real_id(this._device.id);
	}
	public static openDocsLink = (e) => {
		if (get(app)) {
			e.preventDefault();
			const docsURL = e.target.closest('a').href;
			if (get(os) === 'IOS') {
				open_external_link(docsURL);
			} else {
				open_external_link_android(docsURL);
			}
		}
	};
	get deviceID() {
		return this._deviceID;
	}
	public toggleScript = async (scriptID: number, enable: boolean) => {
		const res: ScriptsRequest = await gen2Request(this._device, 'Script.SetConfig', {
			id: scriptID,
			config: { enable },
		});
		if (res?.isok) {
			const setting = devices_settings.getByRealId(this._deviceID);
			if (setting?.[`script:${scriptID}`]) {
				setting[`script:${scriptID}`].enable = enable;
				devices_settings.update_devices_settings(this._deviceID, setting);
			}
		}
		onSettingsResponse(res, this._device, {
			success: this._format('script_toggle_success_toast', {
				default: 'The script has been toggled.',
			}),
			danger: this._format('toggle_scripts_err_toast', {
				default: "Couldn't toggle script.",
			}),
		});
	};
	public runScript = async (scriptID: number, command: string, showToast = true) => {
		const res: ScriptsRequest = await gen2Request(
			this._device,
			`Script.${command == 'start' ? 'Start' : 'Stop'}`,
			{
				id: scriptID,
			},
		);
		if (res?.isok) {
			devices_status.update_single_stat(this._deviceID, {
				key: [`script:${scriptID}`],
				value: { id: scriptID, running: command == 'start' },
			});
			if (command == 'restart') {
				return await this.runScript(scriptID, 'start', false);
			}
		}
		if (showToast) {
			onSettingsResponse(res, this._device, {
				success: this._format(
					command == 'start' ? 'script_run_success_toast' : 'script_stop_success_toast',
					{
						default: `The script has been ${command == 'start' ? 'started' : 'stopped'}.`,
					},
				),
				danger: this._format('script_error_message_toast', {
					values: {
						message: res?.errors?.message || res?.errors || this._format('error_global'),
					},
					default: 'Script error: {message}.',
				}),
			});
		}
		return res;
	};
	public updateScriptName = async (scriptID: number, name: string): Promise<boolean> => {
		const res: ScriptsRequest = await gen2Request(this._device, 'Script.SetConfig', {
			id: scriptID,
			config: { name },
		});
		if (res?.isok) {
			const setting = devices_settings.getByRealId(this._deviceID);
			if (setting?.[`script:${scriptID}`]) {
				setting[`script:${scriptID}`].name = name;
				devices_settings.update_devices_settings(this._deviceID, setting);
			}
		}
		onSettingsResponse(res, this._device, {
			success: this._format('script_name_success_toast', {
				default: 'The script name has been changed.',
			}),
			danger: this._format('script_name_err_toast', {
				default: "The script name hasn't been changed.",
			}),
		});
		return res?.isok;
	};
	public deleteScript = async (scriptID: number): Promise<boolean> => {
		const res: ScriptsRequest = await gen2Request(this._device, 'Script.Delete', { id: scriptID });
		if (res?.isok) {
			devices_status.update_single_stat(this._deviceID, {
				key: [`script:${scriptID}`],
				value: null,
			});
		}
		onSettingsResponse(res, this._device);
		return res?.isok;
	};
	public getScriptsCode = async (scriptID: number): Promise<string> => {
		const chunkRequest = new BytesChunkRequest(this._device, 'Script.GetCode', {
				id: scriptID,
			}),
			res: ScriptsRequest = await chunkRequest.get();
		if (res?.isok) return String(res.data?.data || '');
		onSettingsResponse(res, this._device, {
			danger: this._format('get_scripts_err_toast', {
				default: "Couldn't get script.",
			}),
		});
		return null;
	};
	public getScripts = async (): Promise<{ scripts: Script[]; error: string | null }> => {
		const res: ScriptsRequest = await retryWithBackoff(
			() => gen2Request(this._device, 'Script.List', {}, false),
			{ maxAttempts: 3, initialDelayMs: 500, shouldRetry: (r) => !r?.isok },
		);
		if (res?.isok) {
			return {
				scripts: (res.data?.scripts || res.scripts || []) as Script[],
				error: null,
			};
		}
		onSettingsResponse(res, this._device, {
			danger: this._format('load_scripts_list_err_toast', {
				default: "Couldn't load the scripts' list.",
			}),
		});
		return {
			scripts: [],
			error: this._format('load_scripts_list_err_toast', {
				default: "Couldn't load the scripts' list.",
			}),
		};
	};
	public createScripts = async (name: string, code: string): Promise<number> => {
		let id = null;
		const res: ScriptsRequest = await gen2Request(this._device, 'Script.Create', { name });
		if (res?.isok) {
			id = res.data?.id || res?.id;
			if (id) {
				const status = devices_status.getByRealId(this._deviceID);
				if (status) {
					const setting = devices_settings.getByRealId(this._deviceID);
					if (setting) {
						setting[`script:${id}`] = {
							id,
							enable: false,
							name: name || `script_${id}`,
						};
						devices_settings.update_devices_settings(this._deviceID, setting);
					}
				}
				await this.saveScriptsCode(id, code, true);
			}
			this.dispatchEvent(new Event('create'));
		}
		onSettingsResponse(res, this._device);
		return id;
	};
	public saveScriptsCode = async (
		scriptID: number,
		code: string,
		skipSuccessToast = false,
	): Promise<ScriptsRequest> => {
		if (this._saveScripts) return;
		this._saveScripts = true;
		if (!code) code = ' ';
		const status = devices_status.getByRealId(this._deviceID),
			isRunning = status?.[`script:${scriptID}`]?.running;
		if (isRunning) {
			const res: ScriptsRequest = await this.runScript(scriptID, 'stop', false);
			if (!res?.isok) {
				this._saveScripts = false;
				return;
			}
		}
		let res: ScriptsRequest;
		const parts = Math.ceil((new Blob([code]).size + 64) / 1024),
			length = Math.floor(code.length / parts),
			codeArray = code.match(new RegExp(`(.|[\r\n]){1,${length}}`, 'g'));
		for (let i = 0; i < codeArray.length; i++) {
			res = await gen2Request(this._device, 'Script.PutCode', {
				id: scriptID,
				code: codeArray[i],
				append: i > 0,
			});
			if (!res?.isok) break;
		}
		this._saveScripts = false;
		if (res?.isok && isRunning) {
			await this.runScript(scriptID, 'start', false);
		}
		if (res?.isok && skipSuccessToast) return res;
		onSettingsResponse(res, this._device, {
			success: this._format('script_code_update_success_toast', {
				default: 'The script code has been updated.',
			}),
			danger: this._format('script_error_message_toast', {
				values: {
					message:
						res?.errors?.message ||
						res?.errors ||
						this._format('failed_to_save_script', {
							default: 'Failed to save script',
						}),
				},
				default: 'Script error: {message}!',
			}),
		});
		return res;
	};
	public sendScriptEval = async (scriptID: number, code: string): Promise<string> => {
		const res: ScriptsRequest = await gen2Request(this._device, 'Script.Eval', {
			id: scriptID,
			code,
		});
		return (res.data?.result || res.result) as string;
	};
}
